import { useState, useEffect, useCallback, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { masterAuth } from '../firebase/master';
import { MfaCodeStep } from '../components/MfaCodeStep';
import { isMfaRequired, startSignInMfa, mfaErrorText, type MfaChallenge } from '../utils/mfa';
import { useAuth } from '../context/AuthContext';
import appLogo from '../assets/icon.png';

// Accounts are provisioned by DiaWolt (no self-serve sign-up), so the
// footer points prospective users to get in touch. Same placeholder
// address used on the public Landing page's Contact section.
const CONTACT_EMAIL = 'info@diawolt.com';

function IconAt() {
  return (
    <svg width="17" height="17" viewBox="0 0 20 20" fill="none">
      <circle cx="10" cy="10" r="3.4" stroke="currentColor" strokeWidth="1.5" />
      <path d="M13.4 6.6v4.3c0 1.3.9 2.2 2 2.2 1.4 0 2.4-1.3 2.4-3.4A8 8 0 1 0 12.7 17"
        stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}
function IconLock() {
  return (
    <svg width="17" height="17" viewBox="0 0 20 20" fill="none">
      <rect x="4" y="8.5" width="12" height="8.5" rx="2" stroke="currentColor" strokeWidth="1.5" />
      <path d="M6.5 8.5V6a3.5 3.5 0 0 1 7 0v2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="10" cy="12.5" r="1.1" fill="currentColor" />
    </svg>
  );
}

// ── Rate limiting (client-side) ─────────────────────────────────────────────
const MAX_ATTEMPTS  = 5;
const LOCKOUT_MS    = 5 * 60 * 1000; // 5 minutes
const RL_KEY        = 'dm_rl';

interface RLEntry { attempts: number; lockedUntil: number; }

function getRL(email: string): RLEntry {
  try {
    const all = JSON.parse(localStorage.getItem(RL_KEY) ?? '{}');
    return all[email] ?? { attempts: 0, lockedUntil: 0 };
  } catch { return { attempts: 0, lockedUntil: 0 }; }
}

function setRL(email: string, entry: RLEntry) {
  try {
    const all = JSON.parse(localStorage.getItem(RL_KEY) ?? '{}');
    all[email] = entry;
    localStorage.setItem(RL_KEY, JSON.stringify(all));
  } catch {}
}

function clearRL(email: string) {
  try {
    const all = JSON.parse(localStorage.getItem(RL_KEY) ?? '{}');
    delete all[email];
    localStorage.setItem(RL_KEY, JSON.stringify(all));
  } catch {}
}

// ── Component ────────────────────────────────────────────────────────────────
export default function Login() {
  const { user, isAdmin, isLoading, authError, clearAuthError } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail]         = useState('');
  const [password, setPassword]   = useState('');
  const [error, setError]         = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [lockoutSecs, setLockoutSecs] = useState(0);
  // Set once the password was right but the account has SMS two-step on.
  const [mfa, setMfa]             = useState<MfaChallenge | null>(null);

  // Redirect once authenticated
  useEffect(() => {
    if (!isLoading && user)    navigate('/dashboard',   { replace: true });
    if (!isLoading && isAdmin) navigate('/admin/panel', { replace: true });
  }, [user, isAdmin, isLoading, navigate]);

  // Surface errors from auth context (account disabled, business suspended etc.)
  useEffect(() => {
    if (authError) {
      setError(authError);
      setSubmitting(false);
      clearAuthError();
    }
  }, [authError, clearAuthError]);

  // Lockout countdown ticker
  const checkLockout = useCallback((em: string) => {
    const { lockedUntil } = getRL(em);
    const remaining = Math.ceil((lockedUntil - Date.now()) / 1000);
    if (remaining > 0) {
      setLockoutSecs(remaining);
      return true;
    }
    setLockoutSecs(0);
    return false;
  }, []);

  useEffect(() => {
    if (lockoutSecs <= 0) return;
    const t = setTimeout(() => setLockoutSecs(s => {
      if (s <= 1) { setError(null); return 0; }
      return s - 1;
    }), 1000);
    return () => clearTimeout(t);
  }, [lockoutSecs]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    const normalised = email.trim().toLowerCase();

    // Check lockout before attempting
    if (checkLockout(normalised)) {
      const { lockedUntil } = getRL(normalised);
      const secs = Math.ceil((lockedUntil - Date.now()) / 1000);
      setError(`Too many failed attempts. Try again in ${Math.ceil(secs / 60)} min.`);
      setLockoutSecs(secs);
      return;
    }

    setSubmitting(true);

    try {
      await signInWithEmailAndPassword(masterAuth, normalised, password);
      clearRL(normalised); // success — clear rate limit
      // Auth context takes over from here
    } catch (err: unknown) {
      if (isMfaRequired(err)) {
        // Password was right: clear the counter and ask for the SMS code.
        clearRL(normalised);
        try {
          setMfa(startSignInMfa(err));
        } catch (e) {
          setError(mfaErrorText(e));
        }
        setSubmitting(false);
        return;
      }
      setSubmitting(false);
      const code = (err as { code?: string }).code;

      if (
        code === 'auth/invalid-credential' ||
        code === 'auth/wrong-password'     ||
        code === 'auth/user-not-found'     ||
        code === 'auth/invalid-email'
      ) {
        // Increment attempt counter
        const rl = getRL(normalised);
        const newAttempts = rl.attempts + 1;
        const lockedUntil = newAttempts >= MAX_ATTEMPTS ? Date.now() + LOCKOUT_MS : 0;
        setRL(normalised, { attempts: newAttempts, lockedUntil });

        const remaining = MAX_ATTEMPTS - newAttempts;
        if (lockedUntil > 0) {
          const secs = Math.ceil(LOCKOUT_MS / 1000);
          setLockoutSecs(secs);
          setError(`Too many failed attempts. Try again in ${Math.ceil(secs / 60)} min.`);
        } else {
          setError(`Invalid email or password. ${remaining} attempt${remaining === 1 ? '' : 's'} remaining.`);
        }
      } else {
        setError('Login failed. Please try again.');
      }
    }
  }

  if (isLoading) {
    return (
      <div className="loading-page">
        <span className="spinner" />
        Loading…
      </div>
    );
  }

  const isLocked = lockoutSecs > 0;
  const fmtLock  = `${Math.floor(lockoutSecs / 60)}:${(lockoutSecs % 60).toString().padStart(2, '0')}`;

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-logo">
          <div className="auth-logo-icon">
            <img src={appLogo} alt="" />
          </div>
          <div>
            <h1>DiaWolt</h1>
            <p className="subtitle">{mfa ? 'Two step sign in' : 'Sign in to your account'}</p>
          </div>
        </div>

        {mfa ? (
          <MfaCodeStep
            challenge={mfa}
            onCancel={() => { setMfa(null); setPassword(''); }}
          />
        ) : (<>
        {error && (
          <div className="error-msg">
            {isLocked ? `🔒 ${error} (${fmtLock})` : error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="auth-field">
            <IconAt />
            <input
              id="email"
              type="email"
              autoComplete="email"
              aria-label="Email address"
              placeholder="Enter your email address"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              disabled={submitting || isLocked}
            />
          </div>

          <div className="auth-field">
            <IconLock />
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              aria-label="Password"
              placeholder="Enter your password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              disabled={submitting || isLocked}
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-full"
            disabled={submitting || isLocked}
            style={{ marginTop: 4 }}
          >
            {isLocked
              ? `Locked — ${fmtLock}`
              : submitting
                ? <><span className="spinner" style={{ borderTopColor: 'white' }} /> Signing in…</>
                : 'Sign In'
            }
          </button>

          <p className="auth-alt">
            Need an account? <a href={`mailto:${CONTACT_EMAIL}`}>Send Request</a>
          </p>
        </form>
        </>)}
      </div>
    </div>
  );
}
